import { StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../../web/components-v2/Button';
import { Input } from '../../../../web/components-v2/Input';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { Modal } from '../../../../web/components/Modal';
import { GenerateContractModal } from './GenerateContractModal';
import { GenerateProposalModal } from './GenerateProposalModal';
import { styleSheet } from './styles';
import { PLAN_OPTIONS, PricingCalculationPayload, PricingCalculationResponse } from './types';
import { usePricingCalculation } from './usePricingCalculation';

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

export function PricingWizard({ className, styles }: IProps) {
	const [planType, setPlanType] = React.useState('$349 Client Happiness with AI');
	const [numUsers, setNumUsers] = React.useState(5);
	const [discountPercentage, setDiscountPercentage] = React.useState(35);
	const [state, setState] = React.useState('');
	const [billingCycle, setBillingCycle] = React.useState<'Annual' | 'Quarter'>('Annual');
	const [fbAdsChecked, setFbAdsChecked] = React.useState(false);
	const [fbAdsPremiumChecked, setFbAdsPremiumChecked] = React.useState(false);
	const [gLSAChecked, setGLSAChecked] = React.useState(false);
	const [customContentChecked, setCustomContentChecked] = React.useState(false);
	const [customContentNonprofitChecked, setCustomContentNonprofitChecked] = React.useState(false);
	const [handwritten, setHandwritten] = React.useState(false);
	const [splitBilling, setSplitBilling] = React.useState(false);
	const [result, setResult] = React.useState<PricingCalculationResponse | null>(null);
	const [isProposalModalOpen, setIsProposalModalOpen] = React.useState(false);
	const [isNewContractModalOpen, setIsNewContractModalOpen] = React.useState(false);
	const [isCurrentContractModalOpen, setIsCurrentContractModalOpen] = React.useState(false);
	const [isNoMatchModalOpen, setIsNoMatchModalOpen] = React.useState(false);

	const pricingMutation = usePricingCalculation();

	// Handle plan type changes to disable/enable certain fields
	React.useEffect(() => {
		const plan = planType.toLowerCase();
		const isBundled = plan.includes('fb & glsa bundled') || plan.includes('349 fb');
		const isStandalone = plan.includes('standalone website');

		if (isStandalone || isBundled) {
			setNumUsers(5);
		}

		if (isBundled) {
			setFbAdsChecked(true);
			setGLSAChecked(false);
		}
	}, [planType]);

	const handleCalculate = (e: React.FormEvent) => {
		e.preventDefault();

		const payload: PricingCalculationPayload = {
			billingCycle,
			customContentChecked,

			// Left to avoid the pricing API breaking if these are undefined
			customContentNonprofit: customContentNonprofitChecked,
			discountPercentage,
			fbAdsChecked,
			fbAdsPremiumChecked,
			gLSAChecked,
			handwritten,
			numUsers,
			planType,
			quarterlyVerbiage: billingCycle === 'Quarter' ? '1 of 4 quarterly payments due today' : '',
			special: '',
			splitBilling,
			state: state.trim().toUpperCase() || ' ',
		};

		pricingMutation.mutate(payload, {
			onError: error => {
				toast.error(`Failed to calculate pricing: ${error.message}`);
			},
			onSuccess: data => {
				// Build plan addons string
				const addons = [];
				if (gLSAChecked) addons.push('Google LSA');
				if (fbAdsChecked) addons.push('Meta Ads');
				if (fbAdsPremiumChecked) addons.push('Meta Ads Premium');
				if (customContentChecked) addons.push('Custom Content');
				if (customContentNonprofitChecked) addons.push('Custom Content (Non-Profit)');
				if (handwritten) addons.push('Handwritten Cards');

				const planAddons =
					addons.length === 0
						? ''
						: addons.length === 1
							? addons[0]
							: addons.length === 2
								? addons.join(' & ')
								: addons.slice(0, -1).join(', ') + ' & ' + addons[addons.length - 1];

				const quarterlyVerbiage = billingCycle === 'Quarter' ? '1 of 4 quarterly payments due today' : '';
				const splitBillingVerbiage = splitBilling
					? '1 of 2 payments due upon signup - 2nd payment due 60 days after'
					: '';
				const splitBillingCost = splitBilling ? (data.annualTotal ?? 0) / 2 : 0;
				const discountSubtotal = data.discountAmount ?? 0;

				// Include all data needed for proposal generation (matching original implementation)
				const resultData = {
					...data,
					// Form fields
					billingCycle,
					customContentChecked,
					customContentNonprofit: customContentNonprofitChecked,
					discountPercentage,
					fbAdsChecked,
					fbAdsPremiumChecked,
					gLSAChecked,
					handwritten,
					numUsers,
					planAddons,
					planType,
					quarterlyCost: data.quarterlyTotal ?? 0,
					quarterlyVerbiage,
					splitBilling,
					splitBillingCost,
					splitBillingVerbiage,
					state: state.trim().toUpperCase() || ' ',
					// Calculated fields
					discountSubtotal,
					impFeePrice: data.onboardingFee ?? 0,
					pricingAnnual: data.subtotal ?? 0,
					pricingMonthly: (data.renewalAnnualTotal ?? 0) / 12,
					totalCost: data.annualTotal ?? 0,
				};

				setResult(resultData);

				// Show "No Matches Found" modal if there's no offerUrl
				if (!data.offerUrl) {
					setIsNoMatchModalOpen(true);
				}
			},
		});
	};

	const formatCurrency = (value: number | string | undefined): string => {
		const numericValue = typeof value === 'string' ? parseFloat(value) || 0 : (value ?? 0);
		return numericValue.toLocaleString('en-US', {
			maximumFractionDigits: 2,
			minimumFractionDigits: 2,
		});
	};

	const renderResultRow = (label: string, value: number | undefined, shouldShow?: boolean) => {
		// If shouldShow is explicitly false, hide the row
		if (shouldShow === false) {
			return null;
		}

		const numeric = value ?? 0;
		const optionalFees = ['Meta Ads Fee:', 'Meta Ads Premium Fee:', 'Google LSA Fee:', 'Custom Content Fee:'];

		// For optional fees, if shouldShow is not explicitly provided, hide if $0
		if (shouldShow === undefined && numeric === 0 && optionalFees.includes(label)) {
			return null;
		}

		return (
			<div className={css(styleSheet.resultRow)} key={label}>
				<span className={css(styleSheet.resultLabel)}>{label}</span>
				<span className={css(styleSheet.resultValue)}>${formatCurrency(numeric)}</span>
			</div>
		);
	};

	const selectedPlan = planType.toLowerCase();
	const isFBGLSABundle = selectedPlan.includes('fb & glsa bundled') || selectedPlan.includes('349 fb');
	const isStandaloneWebsite = selectedPlan.includes('standalone website');
	const isUsersDisabled = isStandaloneWebsite || isFBGLSABundle;

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
			<div className={css(styleSheet.layout)}>
				{/* Left: Form Inputs */}
				<form className={css(styleSheet.formSection)} onSubmit={handleCalculate}>
					<div className={css(styleSheet.formGroup)}>
						<label className={css(styleSheet.label)} htmlFor='plan'>
							Plan Type
						</label>
						<select
							className={css(styleSheet.select)}
							id='plan'
							onChange={e => setPlanType(e.target.value)}
							required
							value={planType}
						>
							{PLAN_OPTIONS.map(option => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>

					<div className={css(styleSheet.formGroup)}>
						<label className={css(styleSheet.label)} htmlFor='users'>
							Number of Users
						</label>
						<Input
							styles={[styleSheet.input]}
							disabled={isUsersDisabled}
							id='users'
							min={5}
							onChange={e => setNumUsers(Number(e.target.value))}
							required
							type='number'
							value={String(numUsers)}
						/>
					</div>

					<div className={css(styleSheet.formGroup)}>
						<label className={css(styleSheet.label)} htmlFor='discount'>
							Discount (%)
						</label>
						<Input
							styles={[styleSheet.input]}
							id='discount'
							max={100}
							min={0}
							onChange={e => setDiscountPercentage(Number(e.target.value))}
							required
							type='number'
							value={String(discountPercentage)}
						/>
					</div>

					<div className={css(styleSheet.formGroup)}>
						<label className={css(styleSheet.label)} htmlFor='state'>
							State
						</label>
						<Input
							styles={[styleSheet.input]}
							id='state'
							onChange={e => setState(e.target.value)}
							placeholder='e.g., NC'
							type='text'
							value={state}
						/>
					</div>

					<div className={css(styleSheet.formGroup)}>
						<label className={css(styleSheet.label)} htmlFor='billingCycle'>
							Billing Cycle
						</label>
						<select
							className={css(styleSheet.select)}
							id='billingCycle'
							onChange={e => setBillingCycle(e.target.value as 'Annual' | 'Quarter')}
							required
							value={billingCycle}
						>
							<option value='Annual'>Annual</option>
							<option value='Quarter'>Quarter</option>
						</select>
					</div>

					<div className={css(styleSheet.checkboxGrid)}>
						<label className={css(styleSheet.checkboxLabel)}>
							<input
								checked={fbAdsChecked}
								disabled={isFBGLSABundle || fbAdsPremiumChecked}
								onChange={e => setFbAdsChecked(e.target.checked)}
								type='checkbox'
							/>
							Meta Ads
						</label>
						<label className={css(styleSheet.checkboxLabel)}>
							<input checked={handwritten} onChange={e => setHandwritten(e.target.checked)} type='checkbox' />
							Handwritten Cards
						</label>
						<label className={css(styleSheet.checkboxLabel)}>
							<input
								checked={fbAdsPremiumChecked}
								disabled={fbAdsChecked}
								onChange={e => setFbAdsPremiumChecked(e.target.checked)}
								type='checkbox'
							/>
							Meta Ads Premium
						</label>
						<label className={css(styleSheet.checkboxLabel)}>
							<input
								checked={gLSAChecked}
								disabled={isFBGLSABundle}
								onChange={e => setGLSAChecked(e.target.checked)}
								type='checkbox'
							/>
							Google LSAs
						</label>
						{billingCycle === 'Annual' && (
							<label className={css(styleSheet.checkboxLabel)}>
								<input checked={splitBilling} onChange={e => setSplitBilling(e.target.checked)} type='checkbox' />
								Split Billing
							</label>
						)}
						<label className={css(styleSheet.checkboxLabel)}>
							<input
								checked={customContentChecked}
								onChange={e => setCustomContentChecked(e.target.checked)}
								type='checkbox'
							/>
							Custom Content
						</label>
						<label className={css(styleSheet.checkboxLabel)}>
							<input
								checked={customContentNonprofitChecked}
								onChange={e => setCustomContentNonprofitChecked(e.target.checked)}
								type='checkbox'
							/>
							Custom Content (Non-Profit)
						</label>
					</div>

					<div className={css(styleSheet.buttonRow)}>
						<Button
							disabled={pricingMutation.isPending}
							isLoading={pricingMutation.isPending}
							type='submit'
							variant='primary'
						>
							Calculate
						</Button>
						<Button onClick={() => setIsCurrentContractModalOpen(true)} type='button' variant='primary'>
							Current Client Contract
						</Button>
					</div>
				</form>

				{/* Right: Results Display */}
				<div className={css(styleSheet.resultsSection)}>
					{pricingMutation.isPending && (
						<div className={css(styleSheet.spinnerContainer)}>
							<LoadingSpinner type='small' />
						</div>
					)}

					{!pricingMutation.isPending && !result && (
						<div className={css(styleSheet.resultsContent)}>
							<div className={css(styleSheet.placeholder)}>
								Enter details and click <em>&quot;Calculate&quot;</em> to see pricing
							</div>
						</div>
					)}

					{!pricingMutation.isPending && result && (
						<div className={css(styleSheet.resultsContent)}>
							<div className={css(styleSheet.breakdown)}>
								{renderResultRow('Base Price:', result.basePrice)}
								{renderResultRow('Extra User Cost:', result.extraUserCost)}
								{renderResultRow('Onboarding Fee:', result.onboardingFee)}
								{fbAdsPremiumChecked && renderResultRow('Meta Ads Premium Fee:', result.facebookAdsFee, true)}
								{fbAdsChecked && !fbAdsPremiumChecked && renderResultRow('Meta Ads Fee:', result.facebookAdsFee, true)}
								{gLSAChecked && renderResultRow('Google LSA Fee:', result.googleLSAFee, true)}
								{customContentChecked && renderResultRow('Custom Content Fee:', result.customContentFee, true)}
								{customContentNonprofitChecked && renderResultRow('Custom Content (Non-Profit):', 0, true)}
								{renderResultRow('Subtotal:', result.subtotal)}
								{renderResultRow('Discount:', result.discountAmount)}
								{renderResultRow('Total Before Tax:', result.totalBeforeTaxes)}
								{renderResultRow('Estimated Tax Amount:', result.taxAmount)}
							</div>

							<div className={css(styleSheet.summaryRow)}>
								<div className={css(styleSheet.summaryCard)}>
									<div>
										<strong>Annual Total:</strong> ${formatCurrency(result.annualTotal)}
									</div>
									<div>
										<strong>Quarterly Total:</strong> ${formatCurrency(result.quarterlyTotal)}
									</div>
									<div>
										<strong>Split Billing:</strong> ${formatCurrency(result.splitTotal)}
									</div>
								</div>
								<div className={css(styleSheet.summaryCard)}>
									<div>
										<strong>MRR Amount:</strong> ${formatCurrency((result.renewalAnnualTotal ?? 0) / 12)}
									</div>
									<div>
										<strong>Renewal - Annual:</strong> ${formatCurrency(result.renewalAnnualTotal)}
									</div>
									<div>
										<strong>Renewal - Quarterly:</strong> ${formatCurrency(result.renewalQuarterlyTotal)}
									</div>
								</div>
							</div>

							<div className={css(styleSheet.actionButtons)}>
								<Button onClick={() => setIsProposalModalOpen(true)} type='button' variant='primary'>
									📄 Generate Proposal
								</Button>
								<Button onClick={() => setIsNewContractModalOpen(true)} type='button' variant='primary'>
									📝 Generate Contract
								</Button>
								{result.offerUrl && (
									<a
										className={css(styleSheet.chargifyLink)}
										href={result.offerUrl}
										rel='noopener noreferrer'
										target='_blank'
									>
										➡️ Chargify Link
									</a>
								)}
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Generate Proposal Modal */}
			{result && (
				<GenerateProposalModal
					isOpen={isProposalModalOpen}
					onRequestClose={() => setIsProposalModalOpen(false)}
					pricingData={result}
				/>
			)}

			{/* Generate New Client Contract Modal */}
			{result && (
				<GenerateContractModal
					contractType='new'
					isOpen={isNewContractModalOpen}
					onRequestClose={() => setIsNewContractModalOpen(false)}
					pricingData={result}
				/>
			)}

			{/* Generate Current Client Contract Modal */}
			<GenerateContractModal
				contractType='current'
				isOpen={isCurrentContractModalOpen}
				onRequestClose={() => setIsCurrentContractModalOpen(false)}
			/>

			{/* No Matches Found Modal */}
			<Modal isOpen={isNoMatchModalOpen} onRequestClose={() => setIsNoMatchModalOpen(false)} useDefaultHeader={true}>
				<div className={css(styleSheet.noMatchModal)}>
					<h2 className={css(styleSheet.noMatchTitle)}>No Matches Found</h2>
					<p className={css(styleSheet.noMatchMessage)}>
						This combination doesn&apos;t have a corresponding chargify link in the price-book.
					</p>
					<a
						className={css(styleSheet.noMatchLink)}
						href='https://docs.google.com/forms/d/e/1FAIpQLSerFY52SyTRlNhKfLpwZzHbm5f1SGxALlaSxvpShosDFUJWRA/viewform'
						rel='noopener noreferrer'
						target='_blank'
					>
						Click here to request a custom link
					</a>
					<Button variant='secondary' onClick={() => setIsNoMatchModalOpen(false)}>
						Close
					</Button>
				</div>
			</Modal>
		</div>
	);
}
